import { useEffect, useState } from "react";
import { Plus, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const EMPTY = { scope: "", labor_item: "", unit: "ea", std_hrs: "", basis: "", active: true };

/** EST-FRS-2 §1 — Settings › Cost Library › Labor: the STD LABOR store. Per-part hours stay the first lookup. */
export default function LaborLibraryPanel() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [edits, setEdits] = useState({});
  const [log, setLog] = useState(null);
  const load = () => estApi.getLaborLibrary().then(setData).catch(() => toast.error("Could not load the labor library"));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-4 text-xs text-slate-500">loading…</div>;
  const save = async (row) => {
    const e = edits[row.id] || {};
    try {
      await estApi.updateLabor(row.id, { scope: e.scope ?? row.scope, labor_item: e.labor_item ?? row.labor_item, unit: e.unit ?? row.unit,
                                         std_hrs: Number(e.std_hrs ?? row.std_hrs), basis: e.basis ?? row.basis, active: e.active ?? row.active !== false });
      setEdits((x) => { const n = { ...x }; delete n[row.id]; return n; }); toast.success("Saved — logged"); load();
    } catch (er) { toast.error(er?.response?.data?.detail || "Not saved"); }
  };
  const add = async () => {
    try { await estApi.addLabor({ ...draft, std_hrs: Number(draft.std_hrs) }); setDraft(EMPTY); toast.success("Labor item added"); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Not added"); }
  };
  const cell = (row, k, type = "text", w = "w-full") => (
    <Input type={type} step="0.01" value={edits[row.id]?.[k] ?? row[k] ?? ""} className={`h-7 ${w} text-xs`} data-testid={`labor-${k}-${row.id}`}
           onChange={(e) => setEdits({ ...edits, [row.id]: { ...(edits[row.id] || {}), [k]: e.target.value } })} />);
  return (
    <div className="flex-1 overflow-auto p-3" data-testid="labor-library-panel">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="font-semibold text-slate-800">Labor library</span>
        <span>{data.items.length} items · basis {data.hours_per_man_day}-hour man-day · seeded from FRS STD LABOR</span>
        <span className="text-slate-400">Per-part Labor Hours Per Unit in the cost library is the first lookup; this is the fallback. Every change is logged.</span>
        <button type="button" className="ml-auto underline" data-testid="labor-log-toggle"
                onClick={() => (log ? setLog(null) : estApi.getLaborLog().then(setLog))}>{log ? "hide log" : "change log"}</button>
      </div>
      {log && (
        <ul className="mb-3 max-h-40 overflow-auto rounded border border-slate-200 bg-slate-50 p-2 text-[11px]" data-testid="labor-log">
          {log.map((l) => <li key={l.id}>{l.at.slice(0, 16)} · {l.kind} · {l.actor} · {JSON.stringify(l.detail.changed || l.detail.labor_item || "")}</li>)}
          {!log.length && <li className="text-slate-400">No changes yet.</li>}
        </ul>)}
      <table className="w-full text-xs" data-testid="labor-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
          <tr><th className="px-2 py-1 text-left">Scope</th><th className="px-2 py-1 text-left">Labor item</th><th className="px-2 py-1 text-left">Unit</th>
              <th className="px-2 py-1 text-right">Std hrs</th><th className="px-2 py-1 text-left">Basis / notes</th><th className="px-2 py-1">Active</th><th /></tr>
        </thead>
        <tbody>
          {data.items.map((r) => (
            <tr key={r.id} className={`border-t border-slate-100 ${r.active === false ? "opacity-50" : ""}`} data-testid={`labor-row-${r.id}`}>
              <td className="px-2 py-1 w-20">{cell(r, "scope")}</td>
              <td className="px-2 py-1">{cell(r, "labor_item")}</td>
              <td className="px-2 py-1 w-20">{cell(r, "unit")}</td>
              <td className="px-2 py-1 w-24">{cell(r, "std_hrs", "number")}</td>
              <td className="px-2 py-1">{cell(r, "basis")}</td>
              <td className="px-2 py-1 text-center"><input type="checkbox" checked={edits[r.id]?.active ?? r.active !== false} data-testid={`labor-active-${r.id}`}
                   onChange={(e) => setEdits({ ...edits, [r.id]: { ...(edits[r.id] || {}), active: e.target.checked } })} /></td>
              <td className="px-2 py-1">{edits[r.id] && <Button size="sm" className="h-6 text-[11px]" onClick={() => save(r)} data-testid={`labor-save-${r.id}`}><Save className="mr-1 h-3 w-3" />Save</Button>}</td>
            </tr>))}
          <tr className="border-t border-slate-200 bg-slate-50/60">
            <td className="px-2 py-1"><Input value={draft.scope} placeholder="TRA" className="h-7 text-xs" data-testid="labor-new-scope" onChange={(e) => setDraft({ ...draft, scope: e.target.value })} /></td>
            <td className="px-2 py-1"><Input value={draft.labor_item} placeholder="New labor item" className="h-7 text-xs" data-testid="labor-new-item" onChange={(e) => setDraft({ ...draft, labor_item: e.target.value })} /></td>
            <td className="px-2 py-1"><Input value={draft.unit} className="h-7 text-xs" data-testid="labor-new-unit" onChange={(e) => setDraft({ ...draft, unit: e.target.value })} /></td>
            <td className="px-2 py-1"><Input type="number" step="0.01" value={draft.std_hrs} className="h-7 text-xs" data-testid="labor-new-hrs" onChange={(e) => setDraft({ ...draft, std_hrs: e.target.value })} /></td>
            <td className="px-2 py-1"><Input value={draft.basis} className="h-7 text-xs" data-testid="labor-new-basis" onChange={(e) => setDraft({ ...draft, basis: e.target.value })} /></td>
            <td />
            <td className="px-2 py-1"><Button size="sm" className="h-6 text-[11px]" disabled={!draft.scope || !draft.labor_item || draft.std_hrs === ""} onClick={add} data-testid="labor-add"><Plus className="mr-1 h-3 w-3" />Add</Button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
